import React, { ChangeEvent, FormEvent, useState } from "react";
import { Form } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { hasErrorsApi } from "go-core";
import { ButtonLoading } from "go-form";
import handleException from "go-core/api/handleException";
import { ApiError } from "go-core/api/types";
import { FormErrorMessageApi } from "go-form/components/FormErrorMessage";
import { api } from "../../../services/Security/api";
import { useAuth } from "../../../services/Security/auth";

interface State {
	user_email: string;
	user_name: string;
	user_password: string;
	user_password_confirmation: string;
	user_phone_number: string;
	acceptation_code: string;
	accept_marketing_content: boolean;
	accept_user_terms: boolean;
}

interface Props {
	email: string;
	acceptationCode: string;
	location: any;
}

const RegisterForInvitationForm = (props: Props) => {
	const { t } = useTranslation();
	const auth = useAuth();
	const [errors, setErrors] = useState<ApiError[]>([]);
	const [loading, setLoading] = useState(false);
	const [values, setValues] = useState<State>({
		user_email: props.email,
		user_name: "",
		user_password: "",
		user_password_confirmation: "",
		user_phone_number: "",
		acceptation_code: props.acceptationCode,
		accept_user_terms: false,
		accept_marketing_content: false,
	});

	const handleChange = (prop: keyof State) => (event: ChangeEvent<HTMLInputElement>) => {
		setValues({
			...values,
			[prop]:
				event.target.type === "text" || event.target.type === "password"
					? event.target.value
					: event.target.checked,
		});
	};

	const shouldRedirect = () => {
		return !!(props.location && props.location.state && props.location.state.from);
	};

	const getRedirectUrl = () => {
		return `${props.location.state.from.pathname}${
			props.location.state.from.search && props.location.state.from.search
		}`;
	};
	const handleSubmit = async (event: FormEvent) => {
		event.preventDefault();
		setLoading(true);
		try {
			await api.registerUserByInvitation(values);

			if (shouldRedirect()) {
				await auth.login(values.user_email, values.user_password, false, true, getRedirectUrl());
			} else {
				await auth.login(values.user_email, values.user_password);
			}
		} catch (e) {
			const exceptionErrors = handleException(e);
			setErrors(exceptionErrors);
			setLoading(false);
		}
	};

	return (
		<Form autoComplete="off" noValidate onSubmit={handleSubmit}>
			<>
				<Form.Group className="form-group">
					<Form.Control
						disabled
						id="email"
						isInvalid={hasErrorsApi(errors, "user_email")}
						type="text"
						value={values.user_email}
						placeholder={`${t("lib:common.word.email")} *`}
					/>
					<Form.Label htmlFor="email" sm={3}>
						{t("lib:common.word.email")}
					</Form.Label>
					<FormErrorMessageApi errors={errors} field="user_email" />
				</Form.Group>
				<Form.Group className="form-group">
					<Form.Control
						disabled={loading}
						id="name"
						isInvalid={hasErrorsApi(errors, "user_name")}
						onChange={handleChange("user_name")}
						value={values.user_name}
						placeholder={`${t("modules.security.field.full_name.title")} *`}
						type="text"
					/>
					<Form.Label htmlFor="name" sm={3}>
						{t("modules.security.field.full_name.title")}
					</Form.Label>
					<FormErrorMessageApi errors={errors} field="user_name" />
				</Form.Group>
				<Form.Group className="form-group">
					<Form.Control
						disabled={loading}
						id="password"
						isInvalid={hasErrorsApi(errors, "user_password")}
						onChange={handleChange("user_password")}
						type="password"
						value={values.user_password}
						placeholder={`${t("modules.security.field.password.title")} *`}
					/>
					<Form.Label htmlFor="password" sm={3}>
						{t("modules.security.field.password.title")}
					</Form.Label>
					<FormErrorMessageApi errors={errors} field="user_password" />
				</Form.Group>
				<Form.Group className="form-group">
					<Form.Control
						disabled={loading}
						id="user_password_confirmation"
						isInvalid={hasErrorsApi(errors, "user_password_confirmation")}
						onChange={handleChange("user_password_confirmation")}
						type="password"
						value={values.user_password_confirmation}
						placeholder={`${t("modules.security.field.confirm_password.title")} *`}
					/>
					<Form.Label htmlFor="user_password_confirmation" sm={3}>
						{t("modules.security.field.confirm_password.title")}
					</Form.Label>
					<FormErrorMessageApi errors={errors} field="user_password_confirmation" />
				</Form.Group>
				<Form.Group className="form-group">
					<Form.Control
						disabled={loading}
						id="user_phone_number"
						isInvalid={hasErrorsApi(errors, "user_phone_number")}
						onChange={handleChange("user_phone_number")}
						type="text"
						value={values.user_phone_number}
						placeholder={`${t("lib:common.word.phone_number")} *`}
					/>
					<Form.Label htmlFor="user_phone_number" sm={3}>
						{`${t("lib:common.word.phone_number")} *`}
					</Form.Label>
					<FormErrorMessageApi errors={errors} field="user_phone_number" />
				</Form.Group>
				<Form.Group className="form-group">
					<Form.Check
						required
						type="checkbox"
						name="accept_terms"
						onChange={(e) => {
							setValues({
								...values,
								accept_user_terms: e.target.checked,
							});
						}}
						label={
							<>
								{t("modules.security.field.accept.title")}
								<a href="https://www.gopos.pl/tos">{t("modules.security.field.tos.title")}</a>,{" "}
								<a href="https://www.gopos.pl/privacy-policy">
									{t("modules.security.field.privacy_policy.title")}
								</a>{" "}
								{`${t("modules.security.field.receive_informations_from_gopos.title")} *`}
							</>
						}
						isInvalid={hasErrorsApi(errors, "accept_user_terms")}
						feedback={t("constraints.must_accept")}
						id="accept_user_terms"
					/>
					<FormErrorMessageApi errors={errors} field="accept_terms" />
				</Form.Group>
				<Form.Group className="form-group">
					<Form.Check
						required
						type="checkbox"
						name="accept_marketing_content"
						onChange={(e) => {
							setValues({ ...values, accept_marketing_content: e.target.checked });
						}}
						label={<>{t("modules.security.field.accept_marketing_content.title")}</>}
						isInvalid={hasErrorsApi(errors, "accept_marketing_content")}
						id="accept_marketing_content"
					/>
				</Form.Group>
			</>
			<ButtonLoading loading={loading} type="submit" color="primary">
				{t("modules.security.action.register.title")}
			</ButtonLoading>
		</Form>
	);
};
export default RegisterForInvitationForm;
